/**
 * ConfigProvider — Framework-Agnostic Runtime Configuration
 *
 * No 'use client' directive needed — works in both SSR and browser.
 *
 * All fields are non-optional strings. Required values are validated
 * at server startup (server/config.ts) — the process exits before
 * serving any request if they're missing. Optional values default
 * to empty string, and consumers check for empty before using.
 *
 * On the client, the config is deserialized from window.__CONFIG__
 * which was serialized by the server from the validated config.
 */
import type { SharedWebRuntimeConfig } from '@letsgethome/lib/env';
import { createSafeContext } from '@letsgethome/lib/react/create-safe-context';
import type {
  CdnUrl,
  GoogleMapsApiKey,
  MarketingCdnUrl,
  S3UploadOrigin,
} from '@letsgethome/types/branded';
import type { ReactNode } from 'react';

// Shared imagesUrl / s3Bucket / googleClientId (the branded cross-app trio) come
// from SharedWebRuntimeConfig; only public-specific fields are declared here.
export interface RuntimeConfig extends SharedWebRuntimeConfig {
  siteUrl: string;
  /**
   * REQUIRED, branded — the CDN base for per-post USER-GENERATED content
   * (`getAudioUrl` → `posts/<id>/…`). Minted by `server/config.ts` from the
   * required `CDN_URL` env; in local dev this is MinIO. Distinct brand from
   * {@link RuntimeConfig.marketingCdnUrl}, so a static-marketing builder cannot
   * be fed the (dev-MinIO) UGC base — see `@letsgethome/types/branded`.
   */
  cdnUrl: CdnUrl;
  /**
   * REQUIRED, branded — the CDN base for GLOBAL static-MARKETING assets
   * (chat-demo video, tutorials, posters). Minted by `server/config.ts` from the
   * required `MARKETING_CDN_URL` env. Equals `cdnUrl` in deployed envs; in local
   * dev it points at the staging distribution (marketing assets are not in dev
   * MinIO). The marketing URL builders accept only this brand.
   */
  marketingCdnUrl: MarketingCdnUrl;
  /**
   * Backend API origin the BROWSER talks Connect-RPC to for the realtime-token
   * fetch (`RealtimeService.getToken`, mounting the live conversation
   * WebSocket). Empty in dev (the realtime gateway is reached via the local
   * apis-server / devstack); the full `https://api.<baseDomain>` origin in
   * staging/prod (CSP already allows `https://*.<baseDomain>` + `wss://`).
   * Sourced from the `API_URL` env var (web-public is already a consumer,
   * ADR 0090) — the same origin the server-side `server/connect.ts` uses.
   */
  apiUrl: string;
  /**
   * Bare base domain whose subdomains host this env's public services (no
   * scheme/subdomain/path) — `letsgethome.com` (prod), `staging.letsgethome.com`
   * (staging). The server CSP wildcards (`*.<baseDomain>`, `staff.<baseDomain>`)
   * derive from it, so they are a pure function of the origin SSOT (ADR 0141)
   * — sourced from `PUBLIC_BASE_DOMAIN` (`public_urls.bzl service_base_domain`).
   */
  baseDomain: string;
  /**
   * REQUIRED, branded — minted only by `server/config.ts` from the required
   * `GOOGLE_MAPS_API_KEY` env var. Guaranteed non-empty (the app won't boot
   * otherwise), and the brand means only this validated value can be passed to
   * `<MapApiProvider>`'s `apiKey` — a raw/empty string is a compile error.
   */
  googleMapsApiKey: GoogleMapsApiKey;
  /**
   * REQUIRED, branded, DERIVED — the origin the listing-photo wizard PUTs to
   * (LET-76). Not an env var of its own: `server/config.ts` computes it with
   * `resolveS3UploadOrigin(S3_ENDPOINT, AWS_REGION)`, the same pair the Rust
   * presigner builds its S3 client from, through the endpoint rule projected
   * out of `domain-core`.
   *
   * It is in `RuntimeConfig` — rather than being re-read inside the CSP
   * middleware — for the reason the file header of `server/middleware/csp.ts`
   * gives: an allowlist derived from an independently re-read env var drifts
   * from the URL the page actually emits, and did. Threading the resolved
   * value makes the `connect-src` allowance a pure function of the presigned
   * host.
   */
  s3UploadOrigin: S3UploadOrigin;
}

const [ConfigContextProvider, useConfig] = createSafeContext<RuntimeConfig>('Config');

export { useConfig };

interface ConfigProviderProps {
  children: ReactNode;
  config: RuntimeConfig;
}

export function ConfigProvider({ children, config }: ConfigProviderProps) {
  return <ConfigContextProvider value={config}>{children}</ConfigContextProvider>;
}
